import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash-es';
import VimeoPlayer from 'react-player/vimeo';
import { useTranslation } from '@klv/hooks/useTranslation';

import { BfcmContentCard } from '@klv/molecules/bfcm-content-card';
import Buttons from '@klv/molecules/buttons';
import CarouselButton from '@klv/molecules/carousel-button';
import { Wrapper } from '@klv/utils/wrapper';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import MediaCard from '@klv/molecules/media-card';
import Modal from '@klv/organisms/modal';
import { generateClassName, hash } from '@klv/shared/utils/functions';

import { useModal } from '@klv/context/modal';
import { limitCards } from './utils/functions';

import * as styles from './styles/content-carousel.module.scss';

import type { IContentCarouselProps, ISetUpCarouselIntersectionObserverProps } from './types';

const getCarouselTrackPadding = (carouselTrackRef: React.RefObject<HTMLDivElement | null>) => {
  if (
    window.innerWidth >= 2015
    && carouselTrackRef.current
    && carouselTrackRef.current.childElementCount > 3
  ) {
    const visibleChildren = Array.from(carouselTrackRef.current.children).filter((child) => (child as HTMLElement).offsetParent !== null);

    if (visibleChildren.length > 3) {
      return Math.floor(parseFloat(window.getComputedStyle(carouselTrackRef.current).getPropertyValue('padding-left').replace('px', '')));
    }
  }

  return 0;
};

const setUpObserver = ({
  carouselTrackPadding,
  carouselTrackRef,
  intersectionHandler,
  observerRef,
}: ISetUpCarouselIntersectionObserverProps) => {
  if (observerRef.current) {
    observerRef.current.disconnect();
  }

  const observer = new IntersectionObserver(intersectionHandler, {
    root: carouselTrackRef.current,
    rootMargin: `0px -${carouselTrackPadding}px`,
    threshold: 1,
  });

  if (carouselTrackRef.current) {
    Array.from(carouselTrackRef.current.children).forEach((child) => {
      observer.observe(child);
    });
  }

  observerRef.current = observer;
};

const ContentCarousel = ({
  alignment = 'left', 
  backgroundColor, 
  buttons, 
  cardLimit = 9, 
  carouselBehavior = 'scroll', 
  children, 
  customPadding = { customPaddingBottom: 'default', customPaddingTop: 'default' },
  fadeIn,
  featureCard,
  headerFont = 'sohneDefault', 
  headline = '', 
  mediaCards, 
  narrowColumns = false,
  showFeatureCard = false, 
  subheadline = '', 
}: IContentCarouselProps) => {
  const { t } = useTranslation();

  const backgroundType = getBackgroundType(backgroundColor);

  const { bfcmFeatureCard } = featureCard || {};

  const cards = mediaCards ? limitCards({ cardLimit, mediaCards }) : [];
  const carouselId = `${hash(JSON.stringify({ alignment, backgroundColor, buttons, carouselBehavior, headline, mediaCards, subheadline }))}`;
  const [currentModalVideoUrl, setCurrentModalVideoUrl] = useState('');
  const [currentModalTitle, setCurrentModalTitle] = useState('');
  const [showBackArrow, setShowBackArrow] = useState(false);
  const [showForwardArrow, setShowForwardArrow] = useState(cards?.length > 2 ? true : false);
  const modalStore = useModal();
  const { modalState } = modalStore;
  const canShowModal = (modalState.visibleModalId === carouselId);
  const carouselElId = `carousel-${carouselId}`;

  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const onOpenVideo = useCallback((url: string, title: string) => {
    setCurrentModalVideoUrl(url);
    setCurrentModalTitle(title);
    modalStore.open(carouselId);
  }, [setCurrentModalVideoUrl, setCurrentModalTitle, modalStore, carouselId]);

  const getTrueFirstChild = useCallback(() => {
    const firstChild = carouselTrackRef?.current?.firstChild as HTMLElement;
    const secondChild = firstChild?.nextElementSibling as HTMLElement;

    if (!firstChild) return null;

    const firstElementStyle = window.getComputedStyle(firstChild);
    return firstElementStyle.display === 'none' ? secondChild : firstChild;
  }, [carouselTrackRef]);

  const scroll = useCallback(
    (distance: number) => {
      if (carouselTrackRef?.current) {
        carouselTrackRef.current.scrollBy({ left: distance, behavior: 'smooth' });
      }
    }, [carouselTrackRef]);

  const back = useCallback(() => {
    const firstChild = getTrueFirstChild();
    if (carouselTrackRef?.current && firstChild) {
      scroll(0 - (firstChild).offsetWidth);
    }
  }, [getTrueFirstChild, scroll]);

  const forward = useCallback(() => {
    const firstChild = getTrueFirstChild();
    if (carouselTrackRef?.current && firstChild) {
      scroll((firstChild).offsetWidth);
    }
  }, [getTrueFirstChild, scroll]);

  useEffect(() => {
    const handleIntersection: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (observer.root !== null) {
          const firstChild = getTrueFirstChild();
          if (entry.target === firstChild) {
            setShowBackArrow(entry.intersectionRatio < 1);
          }
          if (entry.target === observer.root.lastElementChild) {
            setShowForwardArrow(entry.intersectionRatio < 1);
          }
        }
      });
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    setUpObserver({
      carouselTrackPadding: getCarouselTrackPadding(carouselTrackRef),
      carouselTrackRef,
      intersectionHandler: handleIntersection,
      observerRef,
    });

    const handleResize = debounce(() => {
      setUpObserver({
        carouselTrackPadding: getCarouselTrackPadding(carouselTrackRef),
        carouselTrackRef,
        intersectionHandler: handleIntersection,
        observerRef,
      });
    }, 500, { trailing: true });

    window.addEventListener('resize', handleResize);

    return () => {
      observerRef?.current?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const HeadlineComponent = headerFont === 'sohneSchmal' ? (
    <div className={styles.styledSchmalHeadline}
      dangerouslySetInnerHTML={{ __html: headline }}
    />
  ) : (
    <h2 className={generateClassName(['h3', styles.styledHeadline])}
      dangerouslySetInnerHTML={{ __html: headline }}
    />
  );

  return (
    <Wrapper className='contentCarousel' contained={false} customPadding={customPadding} fadeIn={fadeIn ? .25 : 0} backgroundColor={backgroundColor}>
      <section
        aria-roledescription="carousel"
        aria-label={headline || 'carousel'}
        className={generateClassName([styles.contentCarousel])}
      >
        <div className={generateClassName(['grid', styles.styledGrid])}>
          <div className={generateClassName(['headlineActionRow', 'grid', styles.styledHeadlineActionRow])}>
            { (headline || subheadline) && (
              <div className={generateClassName([styles.styledHeadlines, alignment === 'left' ? styles.styledHeadlinesLeft : ''])}>
                { HeadlineComponent }
                { subheadline && (
                  <p className={generateClassName(['subheadline', 'p2', styles.styledSubheadline])}>
                    {subheadline}
                  </p>
                )}
              </div>
            )}
            {buttons?.length && (
              <Buttons
                className={generateClassName([styles.styledButtons, alignment === 'left' ? styles.styledButtonsLeft : ''])}
                buttons={buttons}
                buttonColor={backgroundType === 'light' ? 'dark' : 'light'}
                variants={['ghost']}
              />
            )}
          </div>
          {showFeatureCard && featureCard && ( 
            <BfcmContentCard
              className={styles.styledBfcmContentCard}
              font={headerFont}
              desktopImage={bfcmFeatureCard?.contentImage?.desktopImage}
              mobileImage={bfcmFeatureCard?.contentImage?.mobileImage}
              {...bfcmFeatureCard?.contentCard}
            />
          )}
        </div>
        {carouselBehavior === 'scroll' ? (
          <div className={generateClassName([styles.carousel])}
            aria-live="polite"
            id={carouselElId}
          >
            <CarouselButton
              className={generateClassName([styles.styledCarouselButton, showBackArrow ? styles.showCarouselButton : ''])}
              colorVariant={backgroundType}
              direction='left'
              onClick={back}
            />
            <div className={styles.carouselTrack} ref={carouselTrackRef}>
              {!children && (
                cards.map(({ mediaCard }) => (
                  <article 
                    className={generateClassName([
                      styles.carouselItem, 
                      narrowColumns ? styles.carouselItemNarrow : '',
                      'carouselItem',
                    ])}
                    key={JSON.stringify(mediaCard)}
                  >
                    <MediaCard
                      onOpenVideo={onOpenVideo}
                      backgroundColor={backgroundType}
                      {...mediaCard}
                    />
                  </article>
                ))
              )}
              {children}
            </div>
            <CarouselButton
              className={generateClassName([styles.styledCarouselButton, showForwardArrow ? styles.showCarouselButton : ''])}
              colorVariant={backgroundType}
              direction='right'
              onClick={forward}
            />
          </div>
        ) : (
          <div className={generateClassName(['grid', styles.stackedGrid])}>
            {cards.map(({ mediaCard }) => (
              <article 
                className={generateClassName([
                  styles.carouselItem, 
                  narrowColumns ? styles.carouselItemNarrow : '',
                  'carouselItem',
                ])}
                key={JSON.stringify(mediaCard)}
                style={{ width: '100%' }}
              >
                <MediaCard
                  onOpenVideo={onOpenVideo}
                  backgroundColor={backgroundType}
                  buttonVariant='ghost'
                  {...mediaCard}
                />
              </article>
            ))}
          </div>
        )}

        <Modal
          contentPadding="30"
          close={modalStore.close}
          show={canShowModal}
          size="constrained"
          backgroundColor={backgroundType}
          isMediaModal
          title={currentModalTitle}
        >
          <VimeoPlayer
            className={generateClassName([styles.styledVimeoPlayer, 'react-player'])}
            config={{ playerOptions:{ texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage') } } }  
            url={currentModalVideoUrl}
            controls
            playing
            loop={false}
            width="100%"
            height="auto"
          />
        </Modal>
      </section>
    </Wrapper>
  );
};

export default ContentCarousel;
